var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { renderer } from "../RendererGlobals";
import { lightService } from "../light/LightService";
import { Module, isSessionMessage } from "@modules/module";
import { mainScene } from "../mainScene";
import { Matrix4 } from "flavr-math";
import { Group, LineSegments, Mesh, Mesh as ThreeMesh, MeshBasicMaterial, SphereGeometry } from "three";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { BusyIndicator, ConnectionLines, ControllerButton, CreateSubModelMessage, FacetVisibility, hrefHasParameter, LineFacet, MeshFacet, ModuleId, CollisionFacet, PlaySoundMessage, RemoveFacetFromModelMessage, RemoveSubModelMessage, DeleteModelMessage, RenderTransparencyMessage, RenderVisibilityMessage, RequestVisibilityMessage, RequestVisibilityAnswerMessage, REPLAY_ROOM_UID, RoomChangedMessage, SelectEventMessage, TooltipInfoFacet, UpdateFacetOnModelMessage, UTILITY_ROOM_UID, RequestTransparencyMessage, RequestTransparencyAnswerMessage, RenderColorMessage, VisibilityFacet, } from "flavr-common";
import { renderManager } from "@modules/xr/RenderManager";
import { clockService } from "@modules/xr/ClockService";
import { RendererModelInterface, RoomChangeType } from "../renderable/RendererModelInterface";
import { camera } from "../camera";
import { AddHighlightForModelsMessage, AttachRoomToModelMessage, ChangeRoomVisibilityMessage, ChangeTooltipAnchorPointMessage, ChangeTooltipInfoVisibilityMessage, IsXrPresentingAnswerMessage, IsXrPresentingRequestMessage, RemoveHighlightsFromModelsMessage, RequestObject3DAnswerMessage, RequestObject3DMessage, SetClipFadeMessage, SetSubMeshTransformMessage } from "@messages/rendering/MeshMessages";
import { InstanceBatchManager } from "../instancing/BatchManager";
import { AudioBuffersMessage, ControlAudioBufferMessage, MediaTrackMessage } from "@messages/rendering/RendererMessages";
import { EndSessionMessage } from "@messages/session/SessionMessages";
import { soundService } from "../../audio";
import { now } from "@messages/timing";
import { InstanceBatchLineMaterial } from "../instancing/lines/lineMaterials";
import { SelectionHighlightManager } from "@modules/selection/highlighting/SelectionHighlightManager";
import { assertIsWeb } from "@messages/environment";
import { TooltipInfoRenderer } from "../tooltip/TooltipInfoRenderer";
import { RenderableFactory } from "../renderable/RenderableFactory";
import { RendererFacetFactory } from "../renderable/RendererFacetFactory";
import { ShadowSystem } from "../light/ShadowSystem";
import { LightSystem } from "../light/LightSystem";
import { MaterialQualitySystem } from "../material/MaterialQualitySystem";
import { StartPointSystem } from "../startPoint/StartPointSystem";
import { isRenderableType } from "../renderable/Renderable";
import { CameraViewSystem } from "../camera/CameraViewSystem";
import { AvatarSystem } from "../../avatar/AvatarSystem";
import { ReflectionProbeSystem } from "../reflectionProbe/ReflectionProbeSystem";
import { MeshRenderable } from "../mesh/MeshRenderable";
import { CreateCollisionShapeMessage } from "@messages/selection";
import { TrainingVideoSystem } from "@modules/training/renderable/TrainingVideoSystem";
import { modelPool, modelToAbsolutePath } from "@modules/model";
import { meshLoadingTracker } from "../mesh/MeshLoadingTracker";
assertIsWeb("RendererModule");
const RETICLE_POINTER = hrefHasParameter(window.location.href, "reticlepointer");
const UsedFacetTypes = [
    FacetVisibility.typestatic,
    VisibilityFacet.typestatic,
    LineFacet.typestatic,
    ConnectionLines.typestatic,
    BusyIndicator.typestatic,
    ControllerButton.typestatic,
    CollisionFacet.typestatic
];
export var ModelTriggerState;
(function (ModelTriggerState) {
    ModelTriggerState[ModelTriggerState["Created"] = 0] = "Created";
    ModelTriggerState[ModelTriggerState["Destroyed"] = 1] = "Destroyed";
})(ModelTriggerState || (ModelTriggerState = {}));
/**
 * Maintains the runtime Three.js scene so models, render facets, room hierarchy changes and renderer-specific queries
 * stay synchronized with the shared model graph. It builds one `RendererModelInterface` per model and keeps them
 * up to date not only via bus messages but also via direct `ModelPool` callbacks for model transform changes, room
 * changes and model destruction.
 *
 * Facets:
 * - Facet types registered with `RendererFacetFactory` and `RenderableFactory` (including `MeshFacet`), together with
 *   `FacetVisibility`, `VisibilityFacet`, `LineFacet`, `ConnectionLines`, `BusyIndicator`, `ControllerButton` and
 *   `CollisionFacet` - Added or removed facets of these types (delivered as filtered `UpdateFacetOnModelMessage`/
 *   `RemoveFacetFromModelMessage`) are applied to or removed from the model's `RendererModelInterface`, which builds
 *   or updates the corresponding scene objects and instance batches.
 *
 * Messages:
 * - `CreateSubModelMessage` - Creates a `RendererModelInterface` for the new model, applies its initial facets and
 *   any transform that arrived before the model existed.
 * - `RemoveSubModelMessage` and `DeleteModelMessage` - Destroy the model's `RendererModelInterface`, notify model
 *   subscribers and clear its cached transform/animation state.
 * - `RoomChangedMessage` and `ChangeRoomVisibilityMessage` - Update which room's scene content is visible and refresh
 *   every model's in/out-of-room state and shadows.
 * - `RenderVisibilityMessage`, `RenderTransparencyMessage` and `RenderColorMessage` - Change a model's visibility,
 *   transparency (alpha) or material color.
 * - `SetClipFadeMessage` and `SetSubMeshTransformMessage` - Update a model's clip fade value or a specific sub-mesh's
 *   transform.
 * - `SelectEventMessage`, `AddHighlightForModelsMessage` and `RemoveHighlightsFromModelsMessage` - Drive selection
 *   highlighting through the `SelectionHighlightManager`.
 * - `AttachRoomToModelMessage` - Attaches or detaches a room's root node to/from another model.
 * - `ChangeTooltipInfoVisibilityMessage` and `ChangeTooltipAnchorPointMessage` - Show/hide a model's tooltip facet
 *   rendering or reposition its anchor point.
 * - `PlaySoundMessage` - Plays a sound positioned at the model through the `soundService`.
 * - `CreateCollisionShapeMessage` - Creates a `CollisionFacet` on the target model on the renderer's behalf.
 * - `EndSessionMessage` - Resets the renderer's scene graph and internal state for a new session.
 * - `RequestVisibilityMessage`, `RequestTransparencyMessage` and `RequestObject3DMessage` - Answer renderer-specific
 *   queries about a model's visibility, transparency and underlying Three.js object.
 * - `IsXrPresentingRequestMessage` - Reports whether the renderer is currently presenting through XR.
 */
export class RendererModule extends Module {
    isPoseUnavailableSecurityError(error) {
        if (!(error instanceof DOMException))
            return false;
        if (error.name !== "SecurityError")
            return false;
        return error.message.includes("Poses cannot be given out for the current state");
    }
    /**
     * Registers the renderer's sub-systems (avatar, start point, lighting, shadows, material quality, reflection probes,
     * training video), builds the root and persistent-root Three.js scene nodes, wires up the instance batch manager and
     * selection highlight manager, subscribes to model transform/room-change callbacks from `ModelPool` and to all
     * renderer-relevant bus messages, and creates the implicit root model.
     */
    constructor() {
        super(ModuleId.RENDER);
        this.objects3D = new Map();
        this.modelInterfaces = new Map();
        this.roomTransformMatrices = new Map();
        this.animationCallbacks = new Map();
        this.lastXRPoseSecurityErrorLogTime = 0;
        /* private */ this.connectionEvents = {};
        this.transformEvents = new Map();
        this.modelSubscriptions = new Map();
        this.shadowNeedsUpdate = true;
        this.shadowMaterialUpdateNeeded = true;
        this.shadowUpdateWaitingForMeshes = false;
        this.shadowUpdatePendingForEnteredRoom = false;
        this.lastShadowUpdateTime = 0;
        this.pendingModelTransformations = new Map();
        this.pendingMessages = [];
        this.cameraViewSystem = new CameraViewSystem(this);
        this.systems = new Map();
        this.handleIsXrPresentingRequestMessage = (msg, askId) => {
            if (IsXrPresentingRequestMessage.is(msg))
                this.connection.answer(askId, new IsXrPresentingAnswerMessage(renderer.xr.isPresenting));
        };
        this.handleRequestObject3DMessage = (msg, askId) => {
            const mesh = this.getObject3D(msg.uid);
            if (mesh === null || mesh === void 0 ? void 0 : mesh.isObject3D)
                this.connection.answer(askId, new RequestObject3DAnswerMessage(msg.uid, mesh));
            else
                this.connection.answer(askId, new RequestObject3DAnswerMessage(msg.uid, undefined));
        };
        this.handleBusMessage = (message, meta) => {
            if (this.canIgnoreMessage(message, meta)) {
                return;
            }
            if (UpdateFacetOnModelMessage.is(message)) {
                this.onUpdateFacetOnModel(message, meta.timeStamp);
            }
            else if (SelectEventMessage.is(message)) {
                void this.onSelect(message, meta);
            }
            else if (SetClipFadeMessage.is(message)) {
                this.onSetClipFade(message);
            }
            else if (SetSubMeshTransformMessage.is(message)) {
                this.onSetSubMeshTransformMessage(message);
            }
            else if (RemoveFacetFromModelMessage.is(message)) {
                this.onRemoveFacetFromModel(message, meta.timeStamp);
            }
            else if (RenderVisibilityMessage.is(message)) {
                this.onRenderVisibilityMessage(message);
            }
            else if (RenderTransparencyMessage.is(message)) {
                this.onRenderTransparencyMessage(message);
            }
            else if (RenderColorMessage.is(message)) {
                this.onRenderColorMessage(message);
            }
            else if (CreateSubModelMessage.is(message)) {
                this.onCreateSubModel(message, meta.timeStamp);
            }
            else if (RoomChangedMessage.is(message)) {
                this.onRoomChangedMessage(message);
            }
            else if (ChangeRoomVisibilityMessage.is(message)) {
                this.onChangeRoomVisibilityMessage(message);
                // TODO: Check if these are needed, seems to be only relevant for replays
                /* } else if (MediaTrackMessage.is(message)) {
                this.onMediaTrack(message);
              } else if (AudioBuffersMessage.is(message)) {
                this.onAudioBuffersMessage(message);
              } else if (ControlAudioBufferMessage.is(message)) {
                this.onControlAudioBufferMessage(message); */
            }
            else if (PlaySoundMessage.is(message)) {
                this.onPlaySoundMessage(message);
            }
            else if (AttachRoomToModelMessage.is(message)) {
                const model = this.modelInterfaces.get(message.modelUid);
                if (model !== undefined) {
                    if (message.attach) {
                        model.attachRoom(message.roomUid, this.getRootNodeForRoom(message.roomUid), message.transform ? new Matrix4().fromArray(message.transform) : new Matrix4().identity());
                    }
                    else {
                        model.detachRoom(message.roomUid);
                    }
                }
            }
            else if (ChangeTooltipInfoVisibilityMessage.is(message)) {
                const model = this.modelInterfaces.get(message.uid);
                model === null || model === void 0 ? void 0 : model.setFacetVisible(TooltipInfoFacet.typestatic, message.visible);
            }
            else if (ChangeTooltipAnchorPointMessage.is(message)) {
                const model = this.modelInterfaces.get(message.uid);
                const renderer = model === null || model === void 0 ? void 0 : model.getFacetRenderer(TooltipInfoRenderer);
                renderer === null || renderer === void 0 ? void 0 : renderer.updateAnchorPoint(message.anchorPoint);
            }
            else if (AddHighlightForModelsMessage.is(message)) {
                for (const uid of message.modelUids) {
                    const model = this.modelInterfaces.get(uid);
                    if (model !== undefined)
                        void this.selectionHighlightMgr.addSelectionHighlight(message.controllerUid, model, false, message.color);
                }
            }
            else if (RemoveHighlightsFromModelsMessage.is(message)) {
                this.selectionHighlightMgr.removeSelectionHighlights(message.controllerUid, message.modelUids);
            }
            else if (EndSessionMessage.is(message)) {
                this.onReset();
            }
            else if (CreateCollisionShapeMessage.is(message)) {
                const facet = new CollisionFacet(message.shape, message.isSelectable, message.isTrigger);
                this.onUpdateFacetOnModel(new UpdateFacetOnModelMessage(message.uid, facet), meta.timeStamp);
            }
            else {
                console.info("Renderer ignores message: " + message.type);
            }
        };
        /**
         * `ModelPool` transform-changed callback: pushes the model's new world transform to its `RendererModelInterface`,
         * or buffers it in `pendingModelTransformations` if the interface doesn't exist yet.
         */
        this.onModelTransformationUpdate = (model, _transform, meta) => {
            const rendererModelInterface = this.modelInterfaces.get(model.getUID());
            if (rendererModelInterface !== undefined) {
                rendererModelInterface.updateTransform(model.getWorldTransform(), undefined, undefined, meta.timeStamp);
            }
            else {
                this.pendingModelTransformations.set(model.getUID(), model.getWorldTransform());
            }
        };
        /**
         * `ModelPool` room-changed callback: updates the model's `RendererModelInterface` with whether it is now inside
         * the current room, a descendant room of it, or outside of it.
         */
        this.onModelRoomChanged = (model, room) => {
            const rendererModelInterface = this.modelInterfaces.get(model.getUID());
            if (rendererModelInterface === undefined)
                return;
            if (!room.isRoom())
                return;
            const currentRoom = this.currentRoomModelUID !== undefined
                ? modelPool.getRoom(this.currentRoomModelUID)
                : undefined;
            if (currentRoom === undefined || room.getUID() === UTILITY_ROOM_UID) {
                rendererModelInterface.onRoomChanged(RoomChangeType.OUTSIDE_OF_CURRENT);
                return;
            }
            if (room.getUID() === currentRoom.getUID()) {
                rendererModelInterface.onRoomChanged(RoomChangeType.IN_CURRENT);
            }
            else if (currentRoom.isAncestorOf(room)) {
                rendererModelInterface.onRoomChanged(RoomChangeType.IN_DESCENDANT_OF_CURRENT);
            }
            else {
                rendererModelInterface.onRoomChanged(RoomChangeType.OUTSIDE_OF_CURRENT);
            }
        };
        this.onUpdate = () => {
            for (const it of this.animationCallbacks.values()) {
                it(clockService.getDeltaToLastFrameInSeconds());
            }
            if (this.reticlePointer !== undefined) {
                this.reticlePointer.position
                    .set(0, 0, -1)
                    .applyQuaternion(camera.perspectiveCam.quaternion)
                    .multiply(camera.perspectiveCam.scale)
                    .multiplyScalar(100)
                    .add(camera.perspectiveCam.position);
            }
            this.instanceBatchManager.updateAllBatches();
        };
        this.onRender = () => {
            if (document.hidden)
                return true;
            if (this.pendingMessages.length) {
                this.connection.sendMultiple(this.pendingMessages);
                this.pendingMessages = [];
            }
            if (this.shadowNeedsUpdate && meshLoadingTracker.hasPendingLoads()) {
                this.shadowUpdateWaitingForMeshes = true;
            }
            const shadowUpdateTime = this.getSystem(MaterialQualitySystem).shadowUpdateTime;
            if (shadowUpdateTime !== undefined && this.shadowNeedsUpdate && !meshLoadingTracker.hasPendingLoads() && now() - this.lastShadowUpdateTime >= shadowUpdateTime) {
                this.lastShadowUpdateTime = now();
                this.shadowNeedsUpdate = false;
                const updateMaterials = this.shadowMaterialUpdateNeeded;
                this.shadowMaterialUpdateNeeded = false;
                if (this.shadowUpdateWaitingForMeshes) {
                    console.info("[rendering] All meshes loaded, starting shadow recalculation");
                    this.shadowUpdateWaitingForMeshes = false;
                }
                if (this.shadowUpdatePendingForEnteredRoom) {
                    console.info("[rendering] Room entered and meshes loaded, starting shadow recalculation");
                    this.shadowUpdatePendingForEnteredRoom = false;
                }
                mainScene.updateMatrixWorld(true);
                this.getSystem(ShadowSystem).needsUpdate = true;
                this.getSystem(ShadowSystem).update(undefined, updateMaterials);
                this.recalculateShadows();
            }
            this.selectionHighlightMgr.onBeforeRender();
            try {
                renderManager.render();
                const outlineScene = this.selectionHighlightMgr.selectionOutlineScene;
                outlineScene.updateMatrixWorld(true);
                const savedAutoClear = renderer.autoClear;
                renderer.autoClear = false;
                renderer.clearStencil();
                renderer.render(outlineScene, camera.perspectiveCam);
                renderer.autoClear = savedAutoClear;
            }
            catch (error) {
                if (this.isPoseUnavailableSecurityError(error)) {
                    const t = now();
                    if (t - this.lastXRPoseSecurityErrorLogTime > 2000) {
                        this.lastXRPoseSecurityErrorLogTime = t;
                        console.warn("[xr] Pose data temporarily unavailable during render; skipping this frame");
                    }
                    return true;
                }
                throw error;
            }
            finally {
                this.selectionHighlightMgr.onAfterRender();
            }
        };
        this.onRemoveSubModelMessage = (message, meta) => {
            this.removeExistingModel(message.uid, meta.timeStamp);
        };
        this.onUpdateFacetOnModel = (message, timeStamp) => {
            var _a, _b;
            const existing = this.modelInterfaces.get(message.uid);
            if (existing !== undefined) {
                existing.updateFacet(message.facet, timeStamp);
            }
            else {
                const modelName = (_b = (_a = modelPool.getModel(message.uid)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : "unknown";
                console.warn(`[rendering] Cannot update facet ${message.facet.type}, no render model interface stored for ${modelName} (${message.uid})`);
            }
        };
        this.onRequestVisibilityMessage = (msg, askId) => {
            if (RequestVisibilityMessage.is(msg)) {
                const existing = this.modelInterfaces.get(msg.uid);
                let visible = false;
                if (existing) {
                    visible = existing.isVisible();
                }
                this.connection.answer(askId, new RequestVisibilityAnswerMessage(msg.uid, visible));
            }
        };
        this.onRequestTransparencyMessage = (msg, askId) => {
            if (RequestTransparencyMessage.is(msg)) {
                const existing = this.modelInterfaces.get(msg.uid);
                let alpha = 0;
                if (existing) {
                    alpha = this.getAlphaValueOfModel(existing.modelUID);
                }
                this.connection.answer(askId, new RequestTransparencyAnswerMessage(msg.uid, alpha));
            }
        };
        this.registerSystem(AvatarSystem);
        this.registerSystem(StartPointSystem);
        this.registerSystem(LightSystem);
        this.registerSystem(ShadowSystem);
        this.registerSystem(MaterialQualitySystem);
        this.registerSystem(ReflectionProbeSystem);
        this.registerSystem(TrainingVideoSystem);
        this.rootNode = new Group();
        this.rootNode.name = "Renderer Root Node";
        this.rootNode.matrixAutoUpdate = false;
        mainScene.add(this.rootNode);
        this.persistentRootNode = new Group();
        this.persistentRootNode.name = "Renderer Persistent Root Node";
        this.persistentRootNode.matrixAutoUpdate = false;
        this.rootNode.add(this.persistentRootNode);
        this.instanceBatchManager = new InstanceBatchManager(this.rootNode);
        this.selectionHighlightMgr = new SelectionHighlightManager(this.instanceBatchManager);
        modelPool.setOnModelTransformChangedHandler(this.onModelTransformationUpdate);
        modelPool.setOnModelRoomChangedHandler(this.onModelRoomChanged);
        this.connection.subscribeByType(RemoveSubModelMessage, this.onRemoveSubModelMessage);
        this.connection.subscribeByType(DeleteModelMessage, this.onRemoveSubModelMessage);
        this.connection.subscribeForAskRequest(RequestVisibilityMessage, this.onRequestVisibilityMessage);
        this.connection.subscribeForAskRequest(RequestTransparencyMessage, this.onRequestTransparencyMessage);
        const renderFacets = RendererFacetFactory.getFacetTypes();
        const renderableFacets = RenderableFactory.getFacetTypes();
        const facetTypes = [...UsedFacetTypes, ...renderFacets, ...renderableFacets].join("|");
        this.connection.subscribe([
            `${UpdateFacetOnModelMessage.typestatic}(${facetTypes})`,
            SelectEventMessage.typestatic,
            SetClipFadeMessage.typestatic,
            SetSubMeshTransformMessage.typestatic,
            `${RemoveFacetFromModelMessage.typestatic}(${facetTypes})`,
            RenderVisibilityMessage.typestatic,
            CreateSubModelMessage.typestatic,
            RoomChangedMessage.typestatic,
            ChangeRoomVisibilityMessage.typestatic,
            MediaTrackMessage.typestatic,
            AudioBuffersMessage.typestatic,
            ControlAudioBufferMessage.typestatic,
            EndSessionMessage.typestatic,
            PlaySoundMessage.typestatic,
            AttachRoomToModelMessage.typestatic,
            ChangeTooltipInfoVisibilityMessage.typestatic,
            ChangeTooltipAnchorPointMessage.typestatic,
            AddHighlightForModelsMessage.typestatic,
            RemoveHighlightsFromModelsMessage.typestatic,
            RenderTransparencyMessage.typestatic,
            RenderColorMessage.typestatic,
            CreateCollisionShapeMessage.typestatic,
        ], this.handleBusMessage);
        this.connection.subscribeForAskRequest(IsXrPresentingRequestMessage, this.handleIsXrPresentingRequestMessage);
        this.connection.subscribeForAskRequest(RequestObject3DMessage, this.handleRequestObject3DMessage);
        if (RETICLE_POINTER) {
            this.enableReticlePointer(true);
        }
        this.initRootModel();
        // Model callbacks
        modelPool.setOnModelDestroyedHandler((model) => {
            console.debug(`[model] ModelInterface destroyed ${modelToAbsolutePath(model)} via modelPool.setOnModelDestroyedHandler`);
            this.removeExistingModel(model.getUID(), new Date().getTime());
        });
    }
    /**
     * Called when all workers have started. Initializes the shadow system, switches the renderer's shadow map to
     * manual updates, and registers the per-frame update and render callbacks.
     */
    init() {
        this.getSystem(ShadowSystem).init();
        renderer.shadowMap.autoUpdate = false;
        this.updateShadow(true);
        animationFrame$.add(this.onUpdate);
        animationFrame$.onRender$.add(this.onRender);
    }
    registerSystem(RenderSystemTypeClass) {
        this.systems.set(RenderSystemTypeClass, new RenderSystemTypeClass(this));
    }
    /**
     * Returns the registered instance of the requested renderer system, throwing if it was never registered.
     */
    getSystem(RendererSystemTypeClass) {
        const system = this.systems.get(RendererSystemTypeClass);
        if (!system)
            throw new Error(`System with type ${String(RendererSystemTypeClass)} not found`);
        return system;
    }
    /**
     * Returns the Three.js object for the given model UID: its renderable geometry mesh if it has one, otherwise
     * whatever raw `Object3D` was registered for that UID via `addToScene`.
     */
    getObject3D(uid) {
        var _a, _b, _c;
        const mesh = (_c = (_b = (_a = this.modelInterfaces.get(uid)) === null || _a === void 0 ? void 0 : _a.renderable) === null || _b === void 0 ? void 0 : _b.getGeometry()) === null || _c === void 0 ? void 0 : _c.mesh;
        if (mesh !== undefined)
            return mesh;
        return this.objects3D.get(uid);
    }
    /**
     * Iterates over every model that currently has a renderable of the given type, yielding each matching renderable.
     */
    *getRenderables(type) {
        for (const model of this.modelInterfaces.values()) {
            const renderable = model.getRenderableByType(type);
            if (renderable === undefined)
                continue;
            yield renderable;
        }
    }
    /**
     * Flags the renderer's shadow map as needing a recalculation on the next render, if shadows are enabled.
     */
    recalculateShadows() {
        if (renderer.shadowMap.enabled) {
            renderer.shadowMap.needsUpdate = true;
        }
    }
    /**
     * Returns the cached transform matrix for the given room, creating and storing an identity matrix for it if none
     * exists yet.
     */
    getRoomTransformMatrix(roomUid) {
        let matrix = this.roomTransformMatrices.get(roomUid);
        if (matrix === undefined) {
            matrix = new Matrix4();
            this.roomTransformMatrices.set(roomUid, matrix);
        }
        return matrix;
    }
    /**
     * Returns the Three.js root node under which content belonging to the given room is parented.
     */
    getRootNodeForRoom(roomUid) {
        return this.instanceBatchManager.getRootNodeForRoom(roomUid);
    }
    /**
     * Registers a per-frame callback that is invoked with the frame's delta time, keyed by the model/facet pair.
     */
    addAnimationCallback(modelUID, facet, callback) {
        this.animationCallbacks.set(`${modelUID}${facet.typestatic}`, callback);
    }
    /**
     * Unregisters the per-frame animation callback previously added for the given model/facet pair.
     */
    removeAnimationCallback(modelUID, facet) {
        if (facet === undefined || facet.typestatic === undefined)
            return;
        this.animationCallbacks.delete(`${modelUID}${facet.typestatic}`);
    }
    /**
     * Registers a callback that is invoked whenever the target model's `RendererModelInterface` transform updates, and
     * immediately invokes it once with the target's current state if it already exists.
     */
    registerTransformUpdate(uid, facet, targetUid, callback) {
        let events = this.transformEvents.get(targetUid);
        if (events === undefined) {
            this.transformEvents.set(targetUid, (events = new Map()));
        }
        events.set(`${uid}_${targetUid}_${facet.typestatic}`, callback);
        const model = this.modelInterfaces.get(targetUid);
        if (model !== undefined) {
            callback(model);
        }
    }
    /**
     * Registers a callback under the given listener ID that is invoked whenever the model identified by `uid` is
     * created or destroyed, and immediately invokes it with `ModelTriggerState.Created` if the model already exists.
     */
    subscribeModel(listenerUid, uid, callback) {
        let subscriptions = this.modelSubscriptions.get(uid);
        if (subscriptions === undefined) {
            subscriptions = new Map();
            this.modelSubscriptions.set(uid, subscriptions);
        }
        subscriptions.set(listenerUid, callback);
        const model = this.getModel(uid);
        if (model !== undefined) {
            callback(model, ModelTriggerState.Created);
        }
    }
    /**
     * Removes the given listener's subscription registered via `subscribeModel` for the specified model UID.
     */
    unsubscribeModel(uid, listenerUid) {
        const model = this.modelSubscriptions.get(uid);
        if (model === undefined)
            return;
        model.delete(listenerUid);
        if (model.size !== 0)
            return;
        this.modelSubscriptions.delete(uid);
    }
    /**
     * Removes the transform-update callback previously registered via `registerTransformUpdate` for the given
     * uid/facet/target combination.
     */
    unregisterTransformUpdate(uid, facet, targetUid) {
        const events = this.transformEvents.get(targetUid);
        if (events !== undefined) {
            events.delete(`${uid}_${targetUid}_${facet.typestatic}`);
            if (!events.size) {
                this.transformEvents.delete(targetUid);
            }
        }
    }
    /**
     * Creates the implicit `RendererModelInterface` for the model pool's root model, as if a `CreateSubModelMessage`
     * had been received for it.
     */
    initRootModel() {
        // initialize with implicit root model
        const root = modelPool.getRoot();
        const creationMessage = new CreateSubModelMessage(root.getUID());
        creationMessage.path = modelToAbsolutePath(root);
        this.onCreateSubModel(creationMessage, Date.now());
    }
    /**
     * Tears down and rebuilds the renderer's entire scene graph state (root node, instance batches, model interfaces,
     * animation/transform callbacks, current room) for a fresh session, then recreates the root model.
     */
    onReset() {
        mainScene.remove(this.rootNode);
        this.rootNode = new Group();
        // this.rootNode.frustumCulled = false;
        this.rootNode.matrixAutoUpdate = false;
        mainScene.add(this.rootNode);
        this.instanceBatchManager = new InstanceBatchManager(this.rootNode);
        this.modelInterfaces.clear();
        this.animationCallbacks.clear();
        this.roomTransformMatrices.clear();
        this.connectionEvents = {};
        this.transformEvents.clear();
        this.currentRoomModelUID = undefined;
        this.initRootModel();
    }
    /**
     * Reports whether the given model's room is the room that is currently active in the renderer.
     */
    isModelInCurrentRoom(modelUID) {
        var _a, _b;
        const roomUID = (_b = (_a = modelPool.getModel(modelUID)) === null || _a === void 0 ? void 0 : _a.getRoom()) === null || _b === void 0 ? void 0 : _b.getUID();
        return this.currentRoomModelUID !== undefined && this.currentRoomModelUID === roomUID;
    }
    canIgnoreMessage(msg, meta) {
        // ignore server message from other worldTransformer
        if (isSessionMessage(meta)) {
            if (CreateSubModelMessage.is(msg)) {
                return false;
            }
            else if (UpdateFacetOnModelMessage.is(msg) || RemoveSubModelMessage.is(msg) || RemoveFacetFromModelMessage.is(msg)) {
                return !this.modelInterfaces.get(msg.uid);
            }
        }
        return false;
    }
    /**
     * Plays the sound referenced by the message at the world position of the target model, parented under that
     * model's room root node.
     */
    onPlaySoundMessage(msg) {
        const existing = this.modelInterfaces.get(msg.uid);
        if (existing !== undefined) {
            const node = this.getRootNodeForRoom(msg.uid);
            soundService.playSound(node, existing.matrixWorld, msg.url, msg.params);
        }
    }
    /**
     * Creates and adds (or removes) the small red sphere used as an XR reticle pointer.
     */
    enableReticlePointer(enable) {
        if (enable) {
            if (!this.reticlePointer) {
                const reticleMaterial = new MeshBasicMaterial({
                    color: 0xff0000,
                    depthTest: false,
                    depthWrite: false,
                    transparent: true
                });
                this.reticlePointer = new ThreeMesh(new SphereGeometry(0.5, 0.5, 0.5), reticleMaterial);
                this.reticlePointer.renderOrder = 100;
                this.addToScene(this.reticlePointer);
            }
        }
        else {
            if (this.reticlePointer) {
                this.removeFromScene(this.reticlePointer);
            }
        }
    }
    /**
     * Adds an object under the room root of the specified model.
     * Use this for visuals that should participate in room visibility and disappear when their room is hidden.
     */
    addObjectToRoom(node, modelUID) {
        var _a;
        const parentRoom = (_a = modelPool.getModel(modelUID)) === null || _a === void 0 ? void 0 : _a.getRoom();
        if (parentRoom === undefined) {
            console.error("[rendering] RendererModule.addObjectToRoom: Node has no parent room");
            return;
        }
        this.getRootNodeForRoom(parentRoom.getUID()).add(node);
        node.matrixWorldNeedsUpdate = true;
    }
    /**
     * Adds an object under a persistent renderer root that stays visible across room transitions.
     * Use this for player-attached or infrastructure visuals that should not depend on room-root visibility.
     */
    addObjectToPersistentRoot(node) {
        this.persistentRootNode.add(node);
        node.matrixWorldNeedsUpdate = true;
    }
    /**
     * Adds the node under the given model's room root, or under the renderer's root node if no model is specified.
     */
    addToScene(node, modelUID) {
        if (modelUID !== undefined) {
            this.addObjectToRoom(node, modelUID);
        }
        else {
            this.rootNode.add(node);
        }
    }
    /**
     * Sets the opacity of the given model's mesh/line-segment geometry to `1 - alpha`, marking the material transparent
     * and hiding the geometry entirely once fully transparent.
     */
    setAlphaValueOfModel(alpha, modelUID) {
        const opacity = 1 - alpha;
        if (modelUID === undefined || this.modelInterfaces === undefined)
            return;
        const model = this.modelInterfaces.get(modelUID);
        const renderable = model === null || model === void 0 ? void 0 : model.renderable;
        if (renderable === undefined)
            return;
        const owner = renderable.owner;
        if (owner.renderable === undefined || !isRenderableType(MeshRenderable, owner.renderable))
            return;
        const group = owner.renderable.meshInstance.group;
        if (group === undefined)
            return;
        group.traverse((node) => {
            if (node instanceof Mesh || node instanceof LineSegments) {
                node.visible = opacity !== 0;
                node.material.transparent = true;
                node.material.opacity = opacity;
                node.material.needsUpdate = true;
            }
        });
    }
    /**
     * Returns the current alpha (`1 - opacity`) of the given model's mesh/line-segment geometry, or 0 if it has no
     * transparent material or no renderable.
     */
    getAlphaValueOfModel(modelUID) {
        var _a;
        if (modelUID !== undefined && this.modelInterfaces !== undefined) {
            const renderable = (_a = this.modelInterfaces.get(modelUID)) === null || _a === void 0 ? void 0 : _a.renderable;
            if (renderable !== undefined) {
                const ownerRenderable = renderable.owner.renderable;
                if (ownerRenderable === undefined)
                    return 0;
                if (isRenderableType(MeshRenderable, ownerRenderable)) {
                    const group = ownerRenderable.meshInstance.group;
                    if (group === undefined)
                        return 0;
                    if (group instanceof Mesh || group instanceof LineSegments) {
                        if (group.material.transparent)
                            return 1 - group.material.opacity;
                        else
                            return 0;
                    }
                    else {
                        let alpha = 0;
                        group.traverse((node) => {
                            if (node instanceof Mesh || node instanceof LineSegments) {
                                if (node.material.transparent)
                                    alpha = 1 - node.material.opacity;
                            }
                        });
                        return alpha;
                    }
                }
            }
        }
        return 0;
    }
    onRenderColorMessage(message) {
        const model = this.modelInterfaces.get(message.uid);
        if (model !== undefined) {
            this.updateMaterialColorOfModel(model, message.color);
        }
    }
    /**
     * Sets the hex color of every mesh material belonging to the given model, provided its renderable is backed by a
     * `MeshFacet`.
     */
    updateMaterialColorOfModel(model, color) {
        if (model && color) {
            const facetRenderable = model.renderable;
            if (facetRenderable && facetRenderable.facet instanceof MeshFacet) {
                const ownerRenderable = facetRenderable.owner.renderable;
                if (ownerRenderable === undefined)
                    return;
                if (isRenderableType(MeshRenderable, ownerRenderable)) {
                    const group = ownerRenderable.meshInstance.group;
                    if (group === undefined)
                        return;
                    group.traverse((node) => {
                        if (node instanceof Mesh) {
                            node.material.color.setHex(color);
                            node.material.needsUpdate = true;
                        }
                    });
                }
            }
        }
    }
    /**
     * Removes the node from the given model's room root and clears any raw `Object3D` registered for that model UID.
     */
    removeObjectFromRoom(node, modelUID) {
        var _a;
        const parentRoom = (_a = modelPool.getModel(modelUID)) === null || _a === void 0 ? void 0 : _a.getRoom();
        if (parentRoom === undefined) {
            console.error("[rendering] RendererModule.removeObjectFromRoom: Node has no parent room");
            return;
        }
        this.getRootNodeForRoom(parentRoom.getUID()).remove(node);
        this.objects3D.delete(modelUID);
    }
    /**
     * Removes the node from the persistent renderer root added via `addObjectToPersistentRoot`.
     */
    removeObjectFromPersistentRoot(node) {
        this.persistentRootNode.remove(node);
    }
    /**
     * Removes the node from the given model's room root, or from the renderer's root node if no model is specified.
     */
    removeFromScene(node, modelUID) {
        if (modelUID !== undefined) {
            this.removeObjectFromRoom(node, modelUID);
        }
        else {
            this.rootNode.remove(node);
        }
    }
    /**
     * Exposes the module's bus connection so renderer helper classes can send and receive messages through it.
     */
    getConnection() {
        return this.connection;
    }
    /**
     * Returns the shared busy-indicator instance batch for the room containing the given model.
     */
    getBusyIndicatorBatch(modelUID) {
        var _a;
        const parent = (_a = modelPool.getModel(modelUID)) === null || _a === void 0 ? void 0 : _a.getRoom();
        if (parent === undefined)
            throw new Error("Parent room is undefined");
        return this.instanceBatchManager.getBusyIndicatorBatch(parent.getUID());
    }
    /**
     * Returns the instance batch for the given primitive shape/material in the room containing the given model,
     * switching the batch to its reduced-quality material when that room is outside the currently active room.
     */
    getPrimitiveMeshBatch(modelUID, shape, material) {
        const parentRoom = modelPool.getModelOrThrow(modelUID).getRoomOrThrow();
        return this.instanceBatchManager.getPrimitiveMeshBatch(parentRoom.getUID(), shape, material, (batch) => {
            if (parentRoom.getUID() === UTILITY_ROOM_UID)
                return;
            if (this.currentRoomModelUID === undefined)
                return;
            const currentRoom = modelPool.getRoom(this.currentRoomModelUID);
            if (currentRoom !== undefined && this.instanceBatchManager.getShouldBatchUseReducedMaterial(parentRoom, currentRoom)) {
                batch.setUseReducedMaterial(true);
            }
        });
    }
    /**
     * Returns the instance batch for truncated-cone geometry with the given material in the room containing the given
     * model, switching the batch to its reduced-quality material when that room is outside the currently active room.
     */
    getTruncatedConeBatch(modelUID, material) {
        var _a;
        const parentRoom = (_a = modelPool.getModelOrThrow(modelUID)) === null || _a === void 0 ? void 0 : _a.getRoomOrThrow();
        return this.instanceBatchManager.getTruncatedConeBatch(parentRoom.getUID(), material, (batch) => {
            if (parentRoom.getUID() === UTILITY_ROOM_UID)
                return;
            const currentRoom = (this.currentRoomModelUID !== undefined)
                ? modelPool.getRoom(this.currentRoomModelUID)
                : undefined;
            if (currentRoom !== undefined && this.instanceBatchManager.getShouldBatchUseReducedMaterial(parentRoom, currentRoom)) {
                batch.setUseReducedMaterial(true);
            }
        });
    }
    /**
     * Returns the instance batch for lines with the given material in the room containing the given model, setting the
     * batch's visibility based on whether that room is the replay room, an ancestor/descendant of the current room, or
     * not itself a (nested) room at all.
     */
    getLineBatch(modelUID, material = InstanceBatchLineMaterial.FUZZY_LINE) {
        var _a;
        const parentRoom = (_a = modelPool.getModelOrThrow(modelUID)) === null || _a === void 0 ? void 0 : _a.getRoomOrThrow();
        return this.instanceBatchManager.getLineBatch(parentRoom.getUID(), material, (batch) => {
            if (parentRoom.getUID() === UTILITY_ROOM_UID) {
                batch.setVisible(true);
                return;
            }
            if (this.currentRoomModelUID === undefined)
                return;
            const replayRoom = modelPool.getRoomOrThrow(REPLAY_ROOM_UID);
            const currentRoom = modelPool.getRoomOrThrow(this.currentRoomModelUID);
            batch.setVisible(parentRoom.isDescendantOfOrEquals(replayRoom) ||
                parentRoom.isDescendantOfOrEquals(currentRoom) ||
                !(parentRoom.getRoomDepth() > 0 || parentRoom.isRoom()));
        });
    }
    /**
     * Creates a new instance of the given primitive shape in the appropriate instance batch for the model's room.
     */
    createInstancedPrimitiveMesh(modelUID, shape, materialProperties) {
        return this.getPrimitiveMeshBatch(modelUID, shape, materialProperties).createInstance();
    }
    /**
     * Creates a new truncated-cone instance in the appropriate instance batch for the model's room.
     */
    createInstancedTruncatedCone(modelUID, materialProperties) {
        return this.getTruncatedConeBatch(modelUID, materialProperties).createInstance();
    }
    /**
     * Creates a new line instance in the appropriate instance batch for the model's room.
     */
    createInstancedLine(modelUID, material = InstanceBatchLineMaterial.FUZZY_LINE) {
        return this.getLineBatch(modelUID, material).createInstance();
    }
    /**
     * Returns the `RendererModelInterface` registered for the given model UID, if any.
     */
    getModel(uid) {
        return this.modelInterfaces.get(uid);
    }
    onSelect(message, meta) {
        return __awaiter(this, void 0, void 0, function* () {
            const target = this.modelInterfaces.get(message.uid);
            if (target !== undefined) {
                yield this.selectionHighlightMgr.handleSelectEventMessage(target, message, meta);
            }
        });
    }
    onRoomChangedMessage(message) {
        var _a, _b;
        this.selectionHighlightMgr.clearSelectionHighlights();
        const currentRoom = modelPool.getRoomOrThrow(message.uid);
        this.currentRoomModelUID = currentRoom.getUID();
        // Current room should always be visible
        const currentRoomNode = this.getRootNodeForRoom(message.uid);
        if (currentRoomNode !== undefined) {
            currentRoomNode.visible = true;
        }
        lightService.enableDefaultLight(true);
        const parentRoom = currentRoom.getRoom();
        if (parentRoom !== undefined && !parentRoom.isRoot()) {
            const roomRendererModelInterface = this.modelInterfaces.get(this.currentRoomModelUID);
            if (roomRendererModelInterface === undefined) {
                console.error(`Renderer received message: ${JSON.stringify(message)}, but does not have a model for the room ${message.uid}`);
            }
            else {
                // hide the room on enter
                roomRendererModelInterface.setVisible(false);
            }
        }
        let previousRoomModel;
        if (message.prevUid !== undefined) {
            previousRoomModel = modelPool.getRoomOrThrow(message.prevUid);
            if (previousRoomModel !== undefined && !((_a = previousRoomModel.getParent()) === null || _a === void 0 ? void 0 : _a.isRoot())) {
                const previousRoomRendererModelInterface = this.modelInterfaces.get(previousRoomModel.getUID());
                if (previousRoomRendererModelInterface === undefined) {
                    console.error(`Renderer received message: ${JSON.stringify(message)}, but does not have a model for the previous room ${message.prevUid}`);
                }
                else if (currentRoom.isAncestorOf(previousRoomModel)) {
                    // when we zoom out we make the previous room visible again.
                    previousRoomRendererModelInterface.setVisible(true);
                }
            }
            this.changeRoomVisibility(message.prevUid, false);
        }
        this.instanceBatchManager.reducedMaterialForAllOutsideRoom(currentRoom);
        for (const modelInterface of this.modelInterfaces.values()) {
            const parentRoom = (_b = modelPool.getModel(modelInterface.modelUID)) === null || _b === void 0 ? void 0 : _b.getRoom();
            if (parentRoom === undefined || parentRoom.getUID() === UTILITY_ROOM_UID)
                continue;
            if (parentRoom.getUID() === this.currentRoomModelUID) {
                modelInterface.onRoomChanged(RoomChangeType.IN_CURRENT);
            }
            else if (currentRoom.isAncestorOf(parentRoom)) {
                modelInterface.onRoomChanged(RoomChangeType.IN_DESCENDANT_OF_CURRENT);
            }
            else {
                modelInterface.onRoomChanged(RoomChangeType.OUTSIDE_OF_CURRENT);
            }
        }
        this.shadowUpdatePendingForEnteredRoom = true;
        this.updateShadow(true);
    }
    /**
     * Shows or hides the Three.js root node for the room referenced by the message.
     */
    onChangeRoomVisibilityMessage(msg) {
        this.changeRoomVisibility(msg.uid, msg.visible);
    }
    onCreateSubModel(message, timeStamp) {
        // there is a new model...
        let modelInterface = this.modelInterfaces.get(message.uid);
        if (modelInterface !== undefined) {
            modelInterface.createdTime = timeStamp;
            return;
        }
        const model = modelPool.getModel(message.uid);
        if (model === undefined)
            return;
        modelInterface = new RendererModelInterface(model.getUID(), model.getName(), this, timeStamp);
        for (const facet of model.getFacets()) {
            modelInterface.updateFacet(facet, timeStamp);
        }
        this.modelInterfaces.set(message.uid, modelInterface);
        const subscriptions = this.modelSubscriptions.get(model.getUID());
        if (subscriptions !== undefined) {
            for (const callback of subscriptions.values()) {
                callback(modelInterface, ModelTriggerState.Created);
            }
        }
        const pendingTransform = this.pendingModelTransformations.get(message.uid);
        if (pendingTransform !== undefined) {
            modelInterface.updateTransform(pendingTransform, undefined, undefined, timeStamp);
            this.pendingModelTransformations.delete(message.uid);
        }
    }
    onRemoveFacetFromModel(message, timeStamp) {
        var _a, _b;
        const existing = this.modelInterfaces.get(message.uid);
        if (existing !== undefined) {
            existing.removeFacet(message.facetType, timeStamp);
        }
        else {
            const modelName = (_b = (_a = modelPool.getModel(message.uid)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : "unknown";
            console.warn(`[rendering] Cannot remove facet ${message.facetType}, no render model interface stored for ${modelName} (${message.uid})`);
        }
    }
    onRenderVisibilityMessage(message) {
        const existing = this.modelInterfaces.get(message.uid);
        if (existing) {
            existing.setVisible(message.visible);
        }
    }
    onRenderTransparencyMessage(message) {
        const existing = this.modelInterfaces.get(message.uid);
        if (existing === undefined) {
            console.warn(`[rendering] Cannot set transparency for model ${message.uid} because the model is not registered in the renderer`);
            return;
        }
        this.setAlphaValueOfModel(message.alpha, existing.modelUID);
    }
    onSetClipFade(event) {
        const target = this.modelInterfaces.get(event.uid);
        if (target) {
            target.setClipFade(event.target);
        }
    }
    onSetSubMeshTransformMessage(event) {
        const target = this.modelInterfaces.get(event.uid);
        if (target) {
            target.setSubMeshTransform(event.submesh, event.position, event.orientation, event.scale);
        }
    }
    /**
     * Destroys the `RendererModelInterface` for the given model (unless it was recreated after `timeStamp`, in which
     * case only facets up to that time are removed), notifying model subscribers and clearing its cached transform and
     * animation event state.
     */
    removeExistingModel(uid, timeStamp) {
        const existing = this.modelInterfaces.get(uid);
        if (existing === undefined)
            return;
        if (existing.createdTime > timeStamp) {
            existing.removeFacetsUntil(timeStamp);
            return;
        }
        this.pendingModelTransformations.delete(uid);
        this.selectionHighlightMgr.onRemoveModel(uid);
        const subscriptions = this.modelSubscriptions.get(uid);
        if (subscriptions !== undefined) {
            for (const callback of subscriptions.values()) {
                callback(existing, ModelTriggerState.Destroyed);
            }
            this.modelSubscriptions.delete(uid);
        }
        existing.destroy();
        this.modelInterfaces.delete(uid);
        const transEvents = this.transformEvents.get(uid);
        if (transEvents !== undefined) {
            for (const callback of transEvents.values()) {
                callback(existing, true);
            }
            this.transformEvents.delete(uid);
        }
        delete this.connectionEvents[uid];
    }
    /**
     * Marks the shadow map as needing an update on the next render pass (throttled by the active material quality).
     */
    updateShadow(updateMaterials = false) {
        this.shadowNeedsUpdate = true;
        this.shadowMaterialUpdateNeeded || (this.shadowMaterialUpdateNeeded = updateMaterials);
        this.shadowUpdateWaitingForMeshes || (this.shadowUpdateWaitingForMeshes = meshLoadingTracker.hasPendingLoads());
    }
    updateShadowForModel(modelUID, updateMaterials = false) {
        if (!this.isModelInCurrentRoom(modelUID))
            return;
        this.updateShadow(updateMaterials);
    }
    /**
     * Returns the full map of `RendererModelInterface`s currently registered, keyed by model UID.
     */
    getRendererModels() {
        return this.modelInterfaces;
    }
    /// Private helper methods ///
    changeRoomVisibility(roomUID, visible) {
        const node = this.getRootNodeForRoom(roomUID);
        node.visible = visible;
    }
}
